'use client';
import React from 'react';
import { useRouter } from 'next/navigation';
import { useNotificationContext } from '@/app/context/globalNotificationContext';
import Button from '@/app/_components/buttons/Button';
import { mutate } from 'swr';
import { getZocListKey, useAcceptShareZoc } from '@/app/_hooks/fetchers/zoc';

type zocPreviewActionsProps = {
  isLoggedIn: boolean;
  shareCode: string;
  type: 'buttons' | 'list';
};

const ZocPreviewActions = ({
  isLoggedIn,
  shareCode,
}: zocPreviewActionsProps) => {
  const router = useRouter();
  const { setToast } = useNotificationContext();

  const { trigger, isMutating: isLoading } = useAcceptShareZoc(shareCode);

  const handleErrorToast = () => {
    setToast({
      message: 'Could not accept shared zoc.',
      kind: 'error',
      key: `preview-${shareCode}`,
    });
  };

  const handleLoginToAccept = () => {
    router.push('/api/auth/login');
  };

  const handleAccept = async () => {
    try {
      const { data } = await trigger();

      await mutate(getZocListKey());

      router.push(`/zoc/${data.zoc_id}`);
    } catch (e) {
      handleErrorToast();
    }
  };

  const handleClose = () => {
    router.push('/');
  };

  return (
    <div className="flex flex-row">
      {!isLoggedIn && (
        <Button
          variant="dark"
          onClick={() => {
            handleLoginToAccept();
          }}
          label="Login to open in Zoc"
          data-dd-action-name="Log in to accept share"
          dataTestId="login-to-accept"
        />
      )}

      {isLoggedIn && (
        <>
          <Button
            variant="dark"
            onClick={async () => {
              handleAccept();
            }}
            isLoading={isLoading}
            label="Open in Zoc"
            data-dd-action-name="Accept share"
            className="mr-6"
            dataTestId="accept-zoc"
          />
          <Button
            variant="secondary"
            onClick={() => {
              handleClose();
            }}
            label="Cancel"
            data-dd-action-name="Cancel share"
            dataTestId="cancel-zoc-accept"
          />
        </>
      )}
    </div>
  );
};

export { ZocPreviewActions };
